import { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Button, Chip,
  InputAdornment, Alert, MenuItem, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import { useGetSearchMerchantPagedQuery } from '../../../redux/commonApiCalls';
import { useGetTatMerchantsQuery } from '../../../redux/tatApiSlice';
import { resetWizard } from '../../../redux/pricingWizardSlice';

// Fields fetched per page. mappedOrderRule is heavy, so we only ever pull it for
// the current page (server-side pagination), never the whole table. rateCardVersion
// is the authoritative new-flow pricing-commit marker (null until committed).
const INCLUDES = 'id,name,companyName,merchantId,email,city,status,isActive,mappedOrderRule,rateCardVersion';

// Builds the merchant-search body for the current page, status filter and search
// text. Test merchants are always excluded. Search is server-side (regex `like`
// across the text fields; exact match on numeric merchantId).
function buildSearchBody({ page, pageSize, status, query }) {
  const andfilter = [{ field: 'isTestMerchant', operator: 'ne', value: true }];
  if (status && status !== 'ALL') andfilter.push({ field: 'status', operator: 'eq', value: status });
  const orfilter = [];
  const q = (query || '').trim();
  if (q) {
    const rx = `.*${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*`;
    ['companyName', 'name', 'email'].forEach((f) => orfilter.push({ field: f, operator: 'like', value: rx }));
    if (/^\d+$/.test(q)) orfilter.push({ field: 'merchantId', operator: 'eq', value: Number(q) });
  }
  return { andfilter, orfilter, offset: page * pageSize, limit: pageSize, includes: INCLUDES };
}

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'KYC_PENDING', label: 'KYC Pending' },
  { value: 'KYC_UPLOADED', label: 'Docs Uploaded' },
  { value: 'KYC_APPROVED', label: 'KYC Approved' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ACTIVE', label: 'Active' },
];

const STATUS_CONFIG = {
  KYC_PENDING:  { label: 'KYC Pending',   color: 'warning' },
  KYC_UPLOADED: { label: 'Docs Uploaded', color: 'info' },
  KYC_APPROVED: { label: 'KYC Approved',  color: 'success' },
  IN_PROGRESS:  { label: 'In Progress',   color: 'info' },
  ACTIVE:       { label: 'Active',        color: 'success' },
};
const statusMeta = (status) => STATUS_CONFIG[status] || { label: status || '—', color: 'default' };

// Onboarding steps in sequence. The first incomplete step is the merchant's
// "next step"; completed steps are locked (non-clickable).
const STEPS = [
  { key: 'pricing',    label: 'Pricing',    icon: PriceChangeOutlinedIcon },
  { key: 'tat',        label: 'TAT',        icon: ScheduleOutlinedIcon },
  { key: 'allocation', label: 'Allocation', icon: CallSplitOutlinedIcon },
];

function mapRow(m) {
  return {
    id: m.id || m._id,
    merchantId: m.merchantId,
    name: m.name || '—',
    companyName: m.companyName || m.name || '—',
    email: m.email || '—',
    city: m.city || '—',
    status: m.status || 'KYC_PENDING',
    // Step completion — pricing: rateCardVersion is the authoritative new-flow
    // marker. It's null until the pricing wizard commits a rate card (Stage 7
    // execute-commit increments it 1, 2, …). The old sla[].active check was wrong
    // because migrated merchants carry 40+ active legacy courier rate cards that
    // were never set up via the new flow. allocation: an order rule with real
    // mapped rules. TAT is layered in later from the TAT service.
    pricingDone: m.rateCardVersion != null && Number(m.rateCardVersion) > 0,
    allocationDone: !!(m.mappedOrderRule
      && Array.isArray(m.mappedOrderRule.rules)
      && m.mappedOrderRule.rules.length > 0),
  };
}

// First incomplete step in sequence (the actionable "next step"), or null when done.
function nextStepKey(done) {
  const s = STEPS.find((st) => !done[st.key]);
  return s ? s.key : null;
}

export default function MerchantsAllListing() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => { setDebouncedQuery(query); setPage(0); }, 350);
    return () => clearTimeout(id);
  }, [query]);

  const body = useMemo(
    () => buildSearchBody({ page, pageSize, status: statusFilter, query: debouncedQuery }),
    [page, pageSize, statusFilter, debouncedQuery],
  );

  const { data: apiData, isLoading, isFetching, isError, refetch } = useGetSearchMerchantPagedQuery(body, {
    refetchOnMountOrArgChange: true,
  });
  const total = apiData?.count || 0;

  // TAT completion lives in the TAT service — fetch only the 'live' set (light) and
  // join by merchant id (= TAT oid).
  const { data: tatRaw } = useGetTatMerchantsQuery({ tat_status: 'live', limit: 2000 }, { refetchOnMountOrArgChange: true });

  const tatLiveSet = useMemo(() => {
    const list = tatRaw?.data || [];
    return new Set(list.filter((t) => t.tat_status === 'live').map((t) => t.oid));
  }, [tatRaw]);

  // Only the current page's rows — completion chips derive from the heavy
  // sla/mappedOrderRule fields, which we now pull for ~one page at a time.
  const rows = useMemo(() => {
    const list = apiData?.data || [];
    return list.map((m) => {
      const r = mapRow(m);
      const done = { pricing: r.pricingDone, tat: tatLiveSet.has(r.id), allocation: r.allocationDone };
      const completed = STEPS.filter((s) => done[s.key]).length;
      return { ...r, done, completed, next: nextStepKey(done) };
    });
  }, [apiData, tatLiveSet]);

  const goStep = (key, id) => {
    if (key === 'pricing') { dispatch(resetWizard()); navigate(`/merchant-config/pricing-setup?merchantId=${id}`); }
    else if (key === 'tat') navigate(`/merchant-config/tat-setup/${id}`);
    else if (key === 'allocation') navigate(`/merchant-config/d2c-allocation/${id}`);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Merchant',
      minWidth: 230,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word' }}>
            {row.companyName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'JetBrains Mono, monospace', fontSize: 10, wordBreak: 'break-all' }}>
            {row.email !== '—' ? row.email : row.id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'KYC',
      minWidth: 130,
      sortable: false,
      renderCell: ({ row }) => {
        const sc = statusMeta(row.status);
        return <Chip size="small" label={sc.label} color={sc.color} sx={{ fontWeight: 700, fontSize: 11, height: 22 }} />;
      },
    },
    {
      field: 'progress',
      headerName: 'Onboarding Progress',
      minWidth: 430,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ py: 0.5 }}>
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isDone = row.done[step.key];
            const isNext = row.next === step.key;
            if (isDone) {
              // Completed — locked, non-clickable.
              return (
                <Chip
                  key={step.key}
                  size="small"
                  icon={<CheckCircleIcon sx={{ fontSize: '15px !important' }} />}
                  label={step.label}
                  sx={{
                    height: 26, fontWeight: 700, fontSize: 11,
                    bgcolor: (t) => alpha(t.palette.success.main, 0.12),
                    color: 'success.dark',
                    border: (t) => `1px solid ${alpha(t.palette.success.main, 0.35)}`,
                    '& .MuiChip-icon': { color: 'success.main' },
                  }}
                />
              );
            }
            // Pending — actionable. The next step is emphasised (contained); later
            // steps stay outlined so the sequence reads left-to-right.
            return (
              <Tooltip key={step.key} title={isNext ? `Next step — set up ${step.label}` : `Set up ${step.label}`}>
                <Button
                  size="small"
                  disableElevation
                  variant={isNext ? 'contained' : 'outlined'}
                  color="primary"
                  startIcon={<Icon sx={{ fontSize: '14px !important' }} />}
                  onClick={() => goStep(step.key, row.id)}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 11, px: 1, py: 0.4, minWidth: 0, borderRadius: 1.5, ...(isNext ? {} : { borderStyle: 'dashed' }) }}
                >
                  {step.label}
                </Button>
              </Tooltip>
            );
          })}
          <Chip
            size="small"
            label={row.completed === STEPS.length ? 'Complete' : `${row.completed}/${STEPS.length}`}
            sx={{
              ml: 0.5, height: 22, fontWeight: 700, fontSize: 10.5,
              bgcolor: (t) => alpha(row.completed === STEPS.length ? t.palette.success.main : t.palette.grey[500], 0.12),
              color: row.completed === STEPS.length ? 'success.dark' : 'text.secondary',
            }}
          />
        </Stack>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 90,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => (
        <Button
          variant="outlined" size="small" disableElevation
          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: '14px !important' }} />}
          onClick={() => navigate(`/merchant-config/merchant/${row.id}`)}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: 11, px: 1, py: 0.4, minWidth: 0, borderRadius: 1.5 }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Box>
      {isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} action={<Button size="small" onClick={refetch}>Retry</Button>}>
          Failed to load merchants. Please retry.
        </Alert>
      )}

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
        <Stack
          direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1}
          sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <TextField
            size="small"
            placeholder="Search by name, company, email or merchant ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ minWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: 20, bgcolor: 'grey.50' } }}
          />
          <TextField
            select size="small" label="Status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 190 }}
          >
            {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={`${total} merchant${total === 1 ? '' : 's'}`}
            sx={{ fontWeight: 700, bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.dark' }}
          />
        </Stack>
        <ProzoDataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading || isFetching}
          page={page}
          setPage={setPage}
          sizePerPage={pageSize}
          setSizePerPage={setPageSize}
          rowCount={total}
          paginationMode="server"
          getRowHeight={() => 'auto'}
          flat
        />
      </Box>
    </Box>
  );
}
